import {View, Text, StyleSheet} from 'react-native';
import {Image} from 'expo-image';
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {AppDataAchievement} from "@/data/app-data-interfaces";
import {ThemeType} from "@/theming/baseTheme";
import {getTimeSinceAssigned} from "@/lib/utils";
import {Ionicons} from "@expo/vector-icons";
import {useTranslation} from 'react-i18next';

interface AAchievementContentProps {
    achievement: AppDataAchievement;
    size?: 'sm' | 'md' | 'lg';
}

export const AchievementContent = ({achievement, size = 'sm'}: AAchievementContentProps) => {
    const {theme} = useCurrentTheme();
    const {t} = useTranslation();
    const styles = createStyles(theme);

    const timeSinceAssigned = getTimeSinceAssigned(achievement.assigned, t);

    const sizeStyle = styles['achievementIconSize_' + size as keyof typeof styles] as typeof styles.achievementIconSize_sm;
    const fontSizeStyle = styles['achievementFontSize_' + size as keyof typeof styles] as typeof styles.achievementFontSize_sm;
    const fontSizeStyleSmaller = styles['achievementFontSizeSmaller_' + size as keyof typeof styles] as typeof styles.achievementFontSizeSmaller_sm;

    return (
        <View style={styles.achievementItem}>
            <View style={styles.achievementIcon}>
                <Image
                    source={{uri: achievement.icon}}
                    placeholder={{blurhash: achievement.iconBlurhash}}
                    style={[styles.achievementIconImage, sizeStyle]}
                    contentFit="contain"
                />
            </View>
            <View style={styles.achievementContent}>
                <Text style={[styles.achievementTitle, fontSizeStyle]} numberOfLines={1}>{achievement.name}</Text>
                {size !== 'sm' && <Text
                    style={[styles.achievementDescription, fontSizeStyleSmaller]} numberOfLines={2}>{achievement.description}</Text>}
                {timeSinceAssigned && (
                    <View style={styles.earnedContainer}>
                        <Ionicons name="checkmark-circle-outline" size={20} color={theme.colors.secondary}/>
                        <Text style={[styles.earnedText, fontSizeStyleSmaller]}>
                            {t('achievements.earned', { time: timeSinceAssigned })}
                        </Text>
                    </View>
                )}
            </View>
        </View>
    );
};

const createStyles = (theme: ThemeType) => StyleSheet.create({
    achievementItem: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: theme.spacing.sm,
    },

    achievementDescription: {
        color: theme.colors.text + 'aa',
        marginBottom: theme.spacing.sm,
    },

    achievementIcon: {
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: theme.spacing.md,
    },

    achievementIconSize_sm: {
        width: 40,
        height: 40,
        paddingHorizontal: theme.spacing.sm,
    },
    achievementIconSize_md: {
        width: 60,
        height: 60,
        paddingHorizontal: theme.spacing.md,
    },
    achievementIconSize_lg: {
        width: 80,
        height: 80,
        paddingHorizontal: theme.spacing.lg,
    },

    achievementIconImage: {
        width: 24,
        height: 24,
    },

    achievementContent: {
        flex: 1,
    },

    achievementTitle: {
        fontSize: theme.fontSize.sm,
        fontWeight: '600',
        color: theme.colors.text,
        marginBottom: 2,
    },

    achievementFontSize_sm: {
        fontSize: theme.fontSize.md,
    },

    achievementFontSize_md: {
        fontSize: theme.fontSize.lg,
    },

    achievementFontSize_lg: {
        fontSize: theme.fontSize.xl,
    },

    achievementFontSizeSmaller_sm: {
        fontSize: theme.fontSize.xs,
    },

    achievementFontSizeSmaller_md: {
        fontSize: theme.fontSize.sm,
    },

    achievementFontSizeSmaller_lg: {
        fontSize: theme.fontSize.md,
    },

    earnedText: {
        color: theme.colors.textSecondary,
    },
    earnedContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4
    }
});

